const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;


export type MarketType = 'overview' | 'perps' | 'spot';


export interface SyncResult {
  walletId: string;
  fillsFetched: number;
  pages: number;
  portfolioWindowUsed: string | null;
  portfolioPointsStored: number;
  dailyRowsUpserted: number;
}

export interface DashboardKPIs {
  fillsCount: number;
  totalVolume: number;
  totalFees: number;
  accountValue?: number | null;
  pnl?: number | null;
  sharpe: number | null;
  maxDrawdown: number | null;
}

export interface LeverageMetrics {
  avgLeverage: number | null;
  maxLeverage: number | null;
  leverageAdjustedSharpe: number | null;
  skillReturn: number | null;
  rawReturn: number | null;
  leverageMultiplier: number | null;
  dailyLeverage: Array<{ day: string; avgLeverage: number; maxLeverage: number }>;
}

export interface LeverageDailyPoint {
  day: string;
  avgLeverage: number;
  maxLeverage: number;
}

export interface DailyData {
  day: string;
  trades: number;
  volume: number;
  fees: number;
}

export interface AccountValuePoint {
  ts_ms: number;
  value: number;
}

export interface DrawdownPoint {
  ts_ms: number;
  drawdown: number;
}

export interface Fill {
  fill_time_ms: number;
  coin: string | null;
  symbol: string | null;
  side: string | null;
  px: number | null;
  sz: number | null;
  notional: number | null;
  fee: number | null;
}

export interface LeverageSeriesPoint {
  time: string;
  accountValue: number;
  marginUsed: number;
  effectiveLeverage: number;
}

export interface DashboardData {
  kpis: DashboardKPIs;
  leverage: LeverageMetrics;
  leverageSeries: LeverageSeriesPoint[];
  charts: {
    volumeDaily: DailyData[];
    accountValue: AccountValuePoint[];
    drawdown: DrawdownPoint[];
    leverageDaily: LeverageDailyPoint[];
    portfolioWindow: string | null;
  };
  recentFills: Fill[];
}

export interface LiveSnapshot {
  walletId: string;
  ts_ms: number;
  perps: {
    accountValue: number | null;
    marginUsed: number | null;
    unrealizedPnl: number | null;
    state: unknown;
  };
  spot: {
    equity: number | null;
    state: unknown;
  };
}

export async function syncHyperliquidData(
  address: string,
  rangeDays: number = 90,
  captchaToken?: string,
  marketType: MarketType = 'overview'
): Promise<SyncResult> {
  const isDevelopment = import.meta.env.DEV;
  const body: any = { address, rangeDays, marketType, isDevelopment };
  
  // Only include captchaToken in production
  if (!isDevelopment && captchaToken) {
    body.captchaToken = captchaToken;
  }

  const response = await fetch(`${SUPABASE_URL}/functions/v1/hl-sync`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Sync failed');
  }

  return response.json();
}

export async function getLiveSnapshot(address: string): Promise<LiveSnapshot> {
  const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  const response = await fetch(
    `${SUPABASE_URL}/functions/v1/hl-live-snapshot?address=${encodeURIComponent(address)}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        apikey: SUPABASE_PUBLISHABLE_KEY,
        Authorization: `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
      },
    }
  );

  if (!response.ok) {
    let message = 'Failed to fetch live snapshot';
    try {
      const error = await response.json();
      message = error.error || message;
    } catch {
      try {
        const text = await response.text();
        message = text || message;
      } catch {
        // ignore
      }
    }

    throw new Error(`${message} (HTTP ${response.status})`);
  }

  return response.json();
}

export async function getDashboardData(
  address: string,
  rangeDays: number = 30,
  marketType: MarketType = 'overview'
): Promise<DashboardData> {
  const response = await fetch(
    `${SUPABASE_URL}/functions/v1/hl-dashboard?address=${encodeURIComponent(address)}&rangeDays=${rangeDays}&marketType=${encodeURIComponent(marketType)}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch dashboard data');
  }

  return response.json();
}

export async function exportData(
  address: string,
  rangeDays: number = 30,
  type: 'fills' | 'daily' | 'portfolio' = 'fills',
  marketType: MarketType = 'overview'
): Promise<Blob> {
  const response = await fetch(
    `${SUPABASE_URL}/functions/v1/hl-export?address=${encodeURIComponent(address)}&rangeDays=${rangeDays}&type=${type}&marketType=${encodeURIComponent(marketType)}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Export failed');
  }

  return response.blob();
}

export async function getPerpsPositions(address: string): Promise<any> {
  const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  const response = await fetch(
    `${SUPABASE_URL}/functions/v1/hl-perps-positions?address=${encodeURIComponent(address)}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        apikey: SUPABASE_PUBLISHABLE_KEY,
        Authorization: `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
      },
    }
  );

  if (!response.ok) {
    let message = 'Failed to fetch perps positions';
    try {
      const error = await response.json();
      message = error.error || message;
    } catch {
      try {
        const text = await response.text();
        message = text || message;
      } catch {
        // ignore
      }
    }

    throw new Error(`${message} (HTTP ${response.status})`);
  }

  return response.json();
}

export async function getPerpsFunding(address: string, rangeDays: number = 30): Promise<any> {
  const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  const response = await fetch(
    `${SUPABASE_URL}/functions/v1/hl-perps-funding?address=${encodeURIComponent(address)}&rangeDays=${encodeURIComponent(String(rangeDays))}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        apikey: SUPABASE_PUBLISHABLE_KEY,
        Authorization: `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
      },
    }
  );

  if (!response.ok) {
    let message = 'Failed to fetch perps funding';
    try {
      const error = await response.json();
      message = error.error || message;
    } catch {
      try {
        const text = await response.text();
        message = text || message;
      } catch {
        // ignore
      }
    }

    throw new Error(`${message} (HTTP ${response.status})`);
  }

  return response.json();
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function validateEvmAddress(address: string): boolean {
  if (!address || typeof address !== 'string') return false;
  return /^0x[a-fA-F0-9]{40}$/.test(address) && address.length === 42;
}
